import React, { useState, useEffect, useRef } from 'react';
import './BlogsAdmin.css';
import { getApiUrl } from '../config';
import Swal from 'sweetalert2';

const BlogsAdmin = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [currentView, setCurrentView] = useState('list'); // 'list', 'add', 'edit'
  const [activeBlog, setActiveBlog] = useState(null);

  // Authentication states
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    try {
      const stored = localStorage.getItem('admin_user');
      if (!stored) return false;
      const parsed = JSON.parse(stored);
      return parsed && parsed.email === 'Mobin.puri@gmail.com';
    } catch {
      return false;
    }
  });
  const [authMode, setAuthMode] = useState('login'); // 'login' or 'register'
  const [usernameInput, setUsernameInput] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [passwordInput, setPasswordInput] = useState('');
  const [authError, setAuthError] = useState('');

  // Form states
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [content, setContent] = useState('');
  const [image, setImage] = useState('/assets/images/home-banner.jpg');
  const [uploading, setUploading] = useState(false);

  const fileInputRef = useRef(null);

  useEffect(() => {
    if (isLoggedIn) {
      fetchBlogs();
    }
  }, [isLoggedIn]);

  const fetchBlogs = () => {
    setLoading(true);
    fetch(getApiUrl('/blogs'))
      .then(res => res.json())
      .then(data => {
        setBlogs(data);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const handleEditClick = (blog) => {
    // Fetch full blog content by slug first
    fetch(getApiUrl(`/blogs/${blog.slug}`))
      .then(res => res.json())
      .then(data => {
        setActiveBlog(data);
        setTitle(data.title || '');
        setSlug(data.slug || '');
        setExcerpt(data.excerpt || '');
        setMetaTitle(data.meta_title || '');
        setMetaDescription(data.meta_description || '');
        setContent(data.content || '');
        setImage(data.image || '/assets/images/home-banner.jpg');
        setCurrentView('edit');
      })
      .catch(() => {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to load blog detail',
          confirmButtonColor: '#00ca65'
        });
      });
  };

  const handleCreateClick = () => {
    setActiveBlog(null);
    setTitle('');
    setSlug('');
    setExcerpt('');
    setMetaTitle('');
    setMetaDescription('');
    setContent('<p>Your content here...</p>');
    setImage('/assets/images/home-banner.jpg');
    setCurrentView('add');
  };

  const handleTitleChange = (val) => {
    setTitle(val);
    // Auto-generate slug
    setSlug(val.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''));
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('image', file);

    fetch(getApiUrl('/upload'), {
      method: 'POST',
      body: formData
    })
      .then(res => {
        if (!res.ok) throw new Error('Upload failed');
        return res.json();
      })
      .then(data => {
        setImage(data.url);
        setUploading(false);
        Swal.fire({
          icon: 'success',
          title: 'Image Uploaded!',
          text: 'The blog image has been updated.',
          timer: 2000,
          showConfirmButton: false
        });
      })
      .catch(err => {
        Swal.fire({
          icon: 'error',
          title: 'Upload Failed',
          text: err.message,
          confirmButtonColor: '#00ca65'
        });
        setUploading(false);
      });
  };

  const [showHeadingMenu, setShowHeadingMenu] = useState(false);

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const wrapSelectedText = (openTag, closeTag, fallbackText = '') => {
    const textarea = document.querySelector('.content-textarea');
    if (!textarea) {
      setContent((prev) => prev + '\n' + openTag + fallbackText + closeTag);
      return;
    }
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = content.substring(start, end);
    const textToWrap = selectedText || fallbackText;
    const replacement = openTag + textToWrap + closeTag;

    const newContent = content.substring(0, start) + replacement + content.substring(end);
    setContent(newContent);

    setTimeout(() => {
      textarea.focus();
      textarea.selectionStart = start + openTag.length;
      textarea.selectionEnd = start + openTag.length + textToWrap.length;
    }, 0);
  };

  const handleListWrap = () => {
    const textarea = document.querySelector('.content-textarea');
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = content.substring(start, end).trim();
    let listHtml = '';
    if (selectedText) {
      const lines = selectedText.split(/\r?\n/).filter(line => line.trim() !== '');
      const items = lines.map(line => `  <li>${line}</li>`).join('\n');
      listHtml = `<ul>\n${items}\n</ul>\n`;
    } else {
      listHtml = `<ul>\n  <li>List item 1</li>\n  <li>List item 2</li>\n</ul>\n`;
    }

    const newContent = content.substring(0, start) + listHtml + content.substring(end);
    setContent(newContent);
    setTimeout(() => {
      textarea.focus();
      textarea.selectionStart = start;
      textarea.selectionEnd = start + listHtml.length;
    }, 0);
  };

  const insertCtaBlock = () => {
    const ctaSnippet = `\n<div class="inline-cta-box">
  <div class="inline-cta-box__left">
    <h2>Take Your Understanding of the Quran to New Heights – Enroll in Quran Al Lesson Today!</h2>
    <p>Enroll now at Al Quran Lesson and discover new perspectives as you learn the essence of the Quran through our exceptional online courses! Sign up for a free trial today!</p>
    <form class="inline-cta-box__form" action="/free-trial" method="GET">
      <input type="email" placeholder="Your Email" />
      <button type="submit">SUBMIT</button>
    </form>
  </div>
  <div class="inline-cta-box__right">
    <a href="/free-trial/" class="cta-badge-link"><span class="cta-badge">FREE TRIAL</span></a>
  </div>
</div>\n`;
    wrapSelectedText('', '', ctaSnippet);
  };

  const insertTableBlock = () => {
    const tableSnippet = `\n<table class="blog-table">
  <thead>
    <tr>
      <th>Feature</th>
      <th>Details</th>
      <th>Benefit</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td>Flexible Schedule</td>
      <td>24/7 Availability</td>
      <td>Learn at your own convenience</td>
    </tr>
    <tr>
      <td>Certified Tutors</td>
      <td>1-on-1 Online Sessions</td>
      <td>Personalized Quran Learning</td>
    </tr>
  </tbody>
</table>\n`;
    wrapSelectedText('', '', tableSnippet);
  };

  const insertFormBlock = () => {
    const formSnippet = `\n<div class="free-assessment__form-container" style="--bg-url: url('/assets/images/green-bg.png');">
  <form class="free-assessment__form">
    <div class="free-assessment__form-row">
      <input type="text" name="firstName" placeholder="First Name" required class="free-assessment__input" />
      <input type="text" name="lastName" placeholder="Last Name" required class="free-assessment__input" />
    </div>
    <div class="free-assessment__form-row free-assessment__form-row--full">
      <select name="course" required class="free-assessment__select">
        <option value="" disabled selected>Select Course</option>
        <option value="Noorani Qaida">Noorani Qaida</option>
        <option value="Quran Tajweed Classes">Quran Tajweed Classes</option>
        <option value="Quran Reading Classes">Quran Reading Classes</option>
        <option value="Quran Memorization Classes">Quran Memorization Classes</option>
        <option value="Quran Translation Classes">Quran Translation Classes</option>
      </select>
    </div>
    <div class="free-assessment__form-row">
      <select name="country" required class="free-assessment__select">
        <option value="" disabled selected>Select Country</option>
        <option value="USA">USA</option>
        <option value="UK">UK</option>
        <option value="CA">CA</option>
        <option value="Australia">Australia</option>
        <option value="Other">Other</option>
      </select>
      <input type="tel" name="phone" placeholder="Phone No" required class="free-assessment__input" />
    </div>
    <div class="free-assessment__form-row free-assessment__form-row--full">
      <textarea name="message" placeholder="Message" required class="free-assessment__textarea"></textarea>
    </div>
    <button type="submit" class="free-assessment__submit-btn">Submit</button>
  </form>
</div>\n`;
    wrapSelectedText('', '', formSnippet);
  };

  const handleSubmit = (e) => {

    e.preventDefault();
    const payload = {
      title,
      slug,
      excerpt,
      meta_title: metaTitle,
      meta_description: metaDescription,
      content,
      image
    };

    if (currentView === 'add') {
      fetch(getApiUrl('/blogs'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(async res => {
          if (!res.ok) {
            const errData = await res.json().catch(() => ({}));
            throw new Error(errData.error || 'Create failed');
          }
          return res.json();
        })
        .then(() => {
          Swal.fire({
            icon: 'success',
            title: 'Blog Published!',
            text: 'Your blog post has been created and published successfully.',
            confirmButtonColor: '#00ca65'
          });
          setCurrentView('list');
          fetchBlogs();
        })
        .catch(err => {
          Swal.fire({
            icon: 'error',
            title: 'Publish Failed',
            text: err.message,
            confirmButtonColor: '#00ca65'
          });
        });
    } else {
      fetch(getApiUrl(`/blogs/${activeBlog.id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(async res => {
          if (!res.ok) {
            const errData = await res.json().catch(() => ({}));
            throw new Error(errData.error || 'Update failed');
          }
          return res.json();
        })
        .then(() => {
          Swal.fire({
            icon: 'success',
            title: 'Blog Updated!',
            text: 'Your blog changes have been saved successfully.',
            confirmButtonColor: '#00ca65'
          });
          setCurrentView('list');
          fetchBlogs();
        })
        .catch(err => {
          Swal.fire({
            icon: 'error',
            title: 'Update Failed',
            text: err.message,
            confirmButtonColor: '#00ca65'
          });
        });
    }
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: 'Delete Blog Post?',
      text: 'Are you sure you want to delete this blog post? This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#e53935',
      cancelButtonColor: '#757575',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        fetch(getApiUrl(`/blogs/${id}`), {
          method: 'DELETE'
        })
          .then(async res => {
            if (!res.ok) {
              const errData = await res.json().catch(() => ({}));
              throw new Error(errData.error || 'Delete failed');
            }
            return res.json();
          })
          .then(() => {
            Swal.fire({
              icon: 'success',
              title: 'Deleted!',
              text: 'The blog post has been deleted.',
              confirmButtonColor: '#00ca65'
            });
            fetchBlogs();
          })
          .catch(err => {
            Swal.fire({
              icon: 'error',
              title: 'Delete Failed',
              text: err.message,
              confirmButtonColor: '#00ca65'
            });
          });
      }
    });
  };

  const handleAuthSubmit = (e) => {
    e.preventDefault();
    setAuthError('');
    const endpoint = authMode === 'login' ? 'login' : 'register';
    const payload = authMode === 'login'
      ? { username: usernameInput, password: passwordInput }
      : { username: usernameInput, email: emailInput, password: passwordInput };

    fetch(getApiUrl(`/auth/${endpoint}`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(res => {
        if (!res.ok) {
          return res.json().then(d => { throw new Error(d.error || 'Authentication failed'); });
        }
        return res.json();
      })
      .then(data => {
        if (authMode === 'login') {
          localStorage.setItem('admin_user', JSON.stringify(data.user));
          setIsLoggedIn(true);
          setUsernameInput('');
          setPasswordInput('');
        } else {
          Swal.fire({
            icon: 'success',
            title: 'Registration Successful!',
            text: 'Account created. Please log in with your credentials.',
            confirmButtonColor: '#00ca65'
          });
          setAuthMode('login');
          setEmailInput('');
          setPasswordInput('');
        }
      })
      .catch(err => setAuthError(err.message));
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_user');
    setIsLoggedIn(false);
  };

  const filteredBlogs = blogs.filter(b =>
    (b.title || '').toLowerCase().includes(search.toLowerCase()) ||
    (b.slug || '').toLowerCase().includes(search.toLowerCase())
  );

  if (!isLoggedIn) {
    return (
      <div className="admin-auth-page">
        <div className="auth-card">
          <div className="auth-header">
            <h2 className="auth-title">{authMode === 'login' ? '🔑 Admin Login' : '📝 Admin Registration'}</h2>
            <p className="auth-subtitle">Manage blog articles and content settings</p>
          </div>

          <form onSubmit={handleAuthSubmit} className="auth-form">
            {authError && <div className="auth-error-msg">⚠️ {authError}</div>}

            <div className="form-group">
              <label>Username</label>
              <input
                type="text"
                value={usernameInput}
                onChange={(e) => setUsernameInput(e.target.value)}
                placeholder="Enter username"
                required
              />
            </div>

            {authMode === 'register' && (
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  placeholder="Enter email address"
                  required
                />
              </div>
            )}

            <div className="form-group">
              <label>Password</label>
              <input
                type="password"
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                placeholder="Enter password"
                required
              />
            </div>

            <button type="submit" className="auth-submit-btn">
              {authMode === 'login' ? 'Login' : 'Register Account'}
            </button>
          </form>

          <div className="auth-toggle">
            {authMode === 'login' ? (
              <p>New admin account? <button onClick={() => { setAuthMode('register'); setAuthError(''); }} className="toggle-link-btn">Register here</button></p>
            ) : (
              <p>Already have an account? <button onClick={() => { setAuthMode('login'); setAuthError(''); }} className="toggle-link-btn">Login here</button></p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      {/* Top Banner Header */}
      <div className="admin-header">
        <div className="admin-header__container">
          <div className="admin-header__nav">
            {currentView !== 'list' && (
              <button className="admin-back-btn" onClick={() => setCurrentView('list')}>
                ← Back to List
              </button>
            )}
            <button className="admin-logout-btn" onClick={handleLogout}>
              Logout 🚪
            </button>
          </div>
          <a href="/blog/" className="admin-preview-btn" target="_blank" rel="noreferrer">
            👁 View Live Blog
          </a>
        </div>
      </div>

      <div className="admin-content-container">
        {currentView === 'list' ? (
          <div className="admin-list-view">
            <div className="admin-list-controls">
              <div className="admin-search-wrapper">
                <span className="search-icon">🔍</span>
                <input
                  type="text"
                  placeholder="Search blogs by title or slug..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="admin-search-input"
                />
              </div>
              <button className="admin-create-btn" onClick={handleCreateClick}>
                + Create new blog
              </button>
            </div>

            {loading ? (
              <div className="admin-loading">Loading blog list...</div>
            ) : (
              <div className="admin-table-wrapper">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Blog Post</th>
                      <th>Status</th>
                      <th>Created At</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredBlogs.map(blog => (
                      <tr key={blog.id}>
                        <td>
                          <div className="blog-cell">
                            <div className="blog-cell__icon">
                              <img
                                src={blog.image && blog.image.startsWith('http') ? blog.image : `${process.env.PUBLIC_URL}/assets/images/home-banner.jpg`}
                                alt=""
                                style={{ width: '44px', height: '44px', objectFit: 'cover', borderRadius: '8px' }}
                              />
                            </div>
                            <div className="blog-cell__details">
                              <span className="blog-cell__title">{blog.title}</span>
                              <span className="blog-cell__slug">/{blog.slug}/</span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="status-badge status-published">● Published</span>
                        </td>
                        <td>
                          <div>{blog.published_date ? new Date(blog.published_date).toLocaleDateString() : 'N/A'}</div>
                          {blog.updated_date && (
                            <small style={{ color: '#666', display: 'block', fontSize: '11px' }}>
                              Updated: {new Date(blog.updated_date).toLocaleDateString()}
                            </small>
                          )}
                        </td>
                        <td>
                          <div className="actions-cell">
                            <button className="action-btn edit-btn" onClick={() => handleEditClick(blog)} title="Edit">
                              📝
                            </button>
                            <button className="action-btn delete-btn" onClick={() => handleDelete(blog.id)} title="Delete">
                              🗑️
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filteredBlogs.length === 0 && (
                      <tr>
                        <td colSpan="4" style={{ textAlign: 'center', padding: '30px', color: '#777777' }}>
                          No blog posts found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : (
          <div className="admin-form-view">
            <form onSubmit={handleSubmit} className="admin-form-layout">
              {/* Left Column: Form Fields */}
              <div className="admin-form-fields">
                <div className="form-section">
                  <h3 className="section-title">✨ Blog Metadata</h3>

                  <div className="form-group">
                    <label>Blog Title</label>
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => handleTitleChange(e.target.value)}
                      required
                      placeholder="e.g. Top 7 MCA Leads Providing Companies In the USA 2026"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Slug (URL Path)</label>
                      <input
                        type="text"
                        value={slug}
                        onChange={(e) => setSlug(e.target.value)}
                        required
                        placeholder="e.g. top-7-mca-leads-providing-companies"
                      />
                    </div>

                    <div className="form-group">
                      <label>Feature Image</label>
                      <div className="image-input-container">
                        <input
                          type="text"
                          placeholder="Image URL"
                          value={image}
                          onChange={(e) => setImage(e.target.value)}
                          className="image-url-input"
                        />
                        <button type="button" className="image-edit-btn" onClick={triggerFileInput}>
                          {uploading ? 'Uploading...' : '✏️ Edit'}
                        </button>
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleImageUpload}
                          style={{ display: 'none' }}
                          accept="image/*"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Short Description (Meta Excerpt)</label>
                    <textarea
                      value={excerpt}
                      onChange={(e) => setExcerpt(e.target.value)}
                      required
                      rows="3"
                      placeholder="Brief summary of the blog post..."
                    />
                  </div>

                  <div className="form-group">
                    <label>Meta Title</label>
                    <input
                      type="text"
                      value={metaTitle}
                      onChange={(e) => setMetaTitle(e.target.value)}
                      placeholder="e.g. How Online Quran Classes Work | A Complete Guide"
                    />
                  </div>

                  <div className="form-group">
                    <label>Meta Description</label>
                    <textarea
                      value={metaDescription}
                      onChange={(e) => setMetaDescription(e.target.value)}
                      rows="3"
                      placeholder="e.g. Learn how online Quran classes work, including flexible scheduling, live 1-on-1 tutors, interactive tools, and courses for kids & adults."
                    />
                  </div>
                </div>

                <div className="form-section">
                  <h3 className="section-title">⌨️ HTML Content</h3>
                  <p className="section-subtitle">Write or paste your blog HTML structure below. Standard tags like &lt;h2&gt;, &lt;p&gt;, &lt;ul&gt; are supported.</p>

                  {/* Editor Toolbar matching screenshot */}
                  <div className="editor-toolbar">
                    <div className="toolbar-group">
                      <div className="toolbar-dropdown-wrapper">
                        <button
                          type="button"
                          className="toolbar-btn"
                          onClick={() => setShowHeadingMenu(!showHeadingMenu)}
                        >
                          Headings ▾
                        </button>
                        {showHeadingMenu && (
                          <div className="headings-menu">
                            <button type="button" onClick={() => { wrapSelectedText('<h1>', '</h1>', 'Heading 1'); setShowHeadingMenu(false); }}>
                              <span>H1</span> Heading 1
                            </button>
                            <button type="button" onClick={() => { wrapSelectedText('<h2>', '</h2>', 'Heading 2'); setShowHeadingMenu(false); }}>
                              <span>H2</span> Heading 2
                            </button>
                            <button type="button" onClick={() => { wrapSelectedText('<h3>', '</h3>', 'Heading 3'); setShowHeadingMenu(false); }}>
                              <span>H3</span> Heading 3
                            </button>
                            <button type="button" onClick={() => { wrapSelectedText('<h4>', '</h4>', 'Heading 4'); setShowHeadingMenu(false); }}>
                              <span>H4</span> Heading 4
                            </button>
                            <button type="button" onClick={() => { wrapSelectedText('<h5>', '</h5>', 'Heading 5'); setShowHeadingMenu(false); }}>
                              <span>H5</span> Heading 5
                            </button>
                            <button type="button" onClick={() => { wrapSelectedText('<h6>', '</h6>', 'Heading 6'); setShowHeadingMenu(false); }}>
                              <span>H6</span> Heading 6
                            </button>
                          </div>
                        )}
                      </div>

                      <button type="button" className="toolbar-btn" onClick={() => wrapSelectedText('<p>', '</p>', 'Paragraph text here...')}>
                        ¶ Para
                      </button>
                      <button type="button" className="toolbar-btn" onClick={() => wrapSelectedText('<a href="https://alquranlesson.com/free-trial/" style="color: #006100; text-decoration: underline;">', '</a>', 'Link Text')}>
                        🔗 Link
                      </button>
                      <button type="button" className="toolbar-btn" onClick={() => wrapSelectedText('<strong>', '</strong>', 'Bold Text')}>
                        <b>B</b> Bold
                      </button>
                      <button type="button" className="toolbar-btn" onClick={() => wrapSelectedText('<em>', '</em>', 'Italic Text')}>
                        <i>I</i> Italic
                      </button>
                      <button type="button" className="toolbar-btn" onClick={handleListWrap}>
                        ≡ List
                      </button>
                    </div>

                    <div className="toolbar-divider"></div>

                    <div className="toolbar-group">
                      <button type="button" className="toolbar-btn btn-add-cta" onClick={insertCtaBlock}>
                        + Add CTA Block
                      </button>
                      <button type="button" className="toolbar-btn btn-add-table" onClick={insertTableBlock}>
                        ⊞ Add Table
                      </button>
                      <button type="button" className="toolbar-btn btn-add-form" onClick={insertFormBlock}>
                        📋 Add Form
                      </button>
                    </div>

                  </div>

                  <div className="form-group" style={{ marginTop: 0 }}>
                    <textarea
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                      required
                      rows="14"
                      className="content-textarea"
                    />
                  </div>
                </div>

                <div className="form-actions">
                  <button type="submit" className="save-submit-btn">
                    {currentView === 'add' ? 'Publish Blog' : 'Save Changes'}
                  </button>
                </div>
              </div>

              {/* Right Column: Live Interactive Preview */}
              <div className="admin-form-preview">
                <div className="preview-browser">
                  <div className="browser-header">
                    <div className="browser-dots">
                      <span className="dot dot-red"></span>
                      <span className="dot dot-yellow"></span>
                      <span className="dot dot-green"></span>
                    </div>
                    <span className="browser-url">localhost:3000/{slug || 'preview'}/</span>
                  </div>

                  <div className="browser-body">
                    <div
                      className="blog-detail-hero"
                      style={{
                        padding: '60px 20px',
                        backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.65), rgba(0, 0, 0, 0.75)), url('${image.startsWith('http') ? image : process.env.PUBLIC_URL + image}')`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        color: '#ffffff',
                        textAlign: 'center',
                        borderRadius: '8px 8px 0 0'
                      }}
                    >
                      <h1 style={{ fontSize: '1.8rem', margin: '10px 0' }}>{title || 'Blog Title'}</h1>
                      <div style={{ fontSize: '0.85rem', opacity: '0.9' }}>By Admin • {new Date().toLocaleDateString()}</div>
                    </div>

                    <div className="preview-rich-content" style={{ padding: '24px', background: '#ffffff', color: '#333333' }}>
                      <div dangerouslySetInnerHTML={{ __html: content || '<p>No content yet...</p>' }} />
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogsAdmin;
